import {
  fetchJudgmentDiligenceById,
  fetchJudgmentDiligences,
  fetchUnresolvedJudgmentDiligences,
  requestJudgmentDiligence,
  uploadJudgmentDiligenceDocuments,
} from '@/resources/electronicAuctions/judgmentDiligences';

export const types = {
  JUDGMENT_DILIGENCES: 'JUDGMENT_DILIGENCES',
  JUDGMENT_DILIGENCES_UPDATE: 'JUDGMENT_DILIGENCES_UPDATE',
  JUDGMENT_DILIGENCES_UNRESOLVED: 'JUDGMENT_DILIGENCES_UNRESOLVED',
  JUDGMENT_DILIGENCES_UPLOAD: 'JUDGMENT_DILIGENCES_UPLOAD',
  JUDGMENT_DILIGENCES_RESET_UPLOAD: 'JUDGMENT_DILIGENCES_RESET_UPLOAD',
};

const mergeDiligences = (current, incoming) => {
  const map = new Map(current.map(diligence => [diligence.id, diligence]));
  incoming.forEach(diligence => {
    const merged = { ...map.get(diligence.id), ...diligence };
    if (!merged.documentsSent) {
      merged.hasPendingDocuments = false;
    }
    map.set(diligence.id, merged);
  });
  return [...map.values()];
};

const mutations = {
  [types.JUDGMENT_DILIGENCES](state, data) {
    state.diligences = Array.isArray(data) ? data : [];
  },
  [types.JUDGMENT_DILIGENCES_UPDATE](state, data) {
    if (!data) return;
    const incoming = Array.isArray(data) ? data : [data];
    state.diligences = mergeDiligences(state.diligences, incoming);
  },
  [types.JUDGMENT_DILIGENCES_UNRESOLVED](state, data) {
    state.unresolvedItems = Array.isArray(data) ? data : [];
  },
  [types.JUDGMENT_DILIGENCES_UPLOAD](state, progress) {
    state.uploadProgress = Math.round((progress.loaded / progress.total) * 100);
  },
  [types.JUDGMENT_DILIGENCES_RESET_UPLOAD](state) {
    state.uploadProgress = 0;
  },
};

const actions = {
  async fetchJudgmentDiligences({ commit }, { auctionId, activeOnly = false }) {
    const response = await fetchJudgmentDiligences(auctionId, { activeOnly });
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.JUDGMENT_DILIGENCES, response.data);
  },
  async fetchUnresolvedJudgmentDiligences({ commit }, { auctionId }) {
    const response = await fetchUnresolvedJudgmentDiligences(auctionId);
    if (response.status !== 200) return Promise.reject(response.data);
    return commit(types.JUDGMENT_DILIGENCES_UNRESOLVED, response.data);
  },
  async fetchJudgmentDiligenceById(_, { auctionId, diligenceId }) {
    const response = await fetchJudgmentDiligenceById(auctionId, diligenceId);
    if (response.status !== 200) return Promise.reject(response.data);
    return response.data;
  },
  async requestJudgmentDiligence({ commit }, { auctionId, itemIds, description }) {
    const response = await requestJudgmentDiligence(auctionId, { itemIds, description });
    if (response.status !== 201) return Promise.reject(response.data);
    commit(types.JUDGMENT_DILIGENCES_UPDATE, response.data);
    return response.data;
  },
  async uploadJudgmentDiligenceDocuments({ commit }, { auctionId, diligenceId, data }) {
    const response = await uploadJudgmentDiligenceDocuments(auctionId, diligenceId, data, {
      onUploadProgress: progress => commit(types.JUDGMENT_DILIGENCES_UPLOAD, progress),
    });
    if (response.status !== 201) return Promise.reject(response.data);
    commit(types.JUDGMENT_DILIGENCES_RESET_UPLOAD);
    return response.data;
  },
  updateJudgmentDiligences({ commit }, data) {
    commit(types.JUDGMENT_DILIGENCES_UPDATE, data);
  },
  refreshUnresolvedFromDiligences({ state, commit }) {
    const unresolved = [];

    state.diligences
      .filter(
        diligence => diligence.active && diligence.documentsSent && diligence.hasPendingDocuments,
      )
      .forEach(diligence => {
        diligence.items
          .filter(item => item.active)
          .forEach(item => {
            unresolved.push({
              auctionItemId: item.auctionItemId,
              bidderId: diligence.bidderId,
            });
          });
      });

    commit(types.JUDGMENT_DILIGENCES_UNRESOLVED, unresolved);
  },
  resetJudgmentDiligencesUpload({ commit }) {
    commit(types.JUDGMENT_DILIGENCES_RESET_UPLOAD);
  },
};

const getters = {
  hasUnresolvedDiligenceForItem: state => itemId =>
    state.diligences.some(
      diligence =>
        diligence.active &&
        diligence.documentsSent &&
        diligence.hasPendingDocuments &&
        diligence.items.some(item => item.active && item.auctionItemId === itemId),
    ),
  activeDiligenceForItemBidder: state => (itemId, bidderId) =>
    state.diligences.find(
      diligence =>
        diligence.active &&
        diligence.bidderId === bidderId &&
        diligence.items.some(item => item.active && item.auctionItemId === itemId),
    ),
};

const state = {
  diligences: [],
  unresolvedItems: [],
  uploadProgress: 0,
};

export default {
  namespaced: true,
  state,
  mutations,
  actions,
  getters,
};
